/**
 * Alertas globais de conexão: modal WhatsApp (desconectado/pendente/ausente)
 * + toasts de expiração Meta. Sem UI quando não há problema.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCrmData } from '../../contexts/CrmProviders';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAppContext } from '../../contexts/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import { notify } from '../../utils/toast';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { AppIcon } from '../icons/AppIcon';
import type { WhatsappConnection } from '../../types';
import {
  normalizeWhatsappStatusBucket,
  type WhatsappStatusBucket,
} from '../../domain/superadmin/whatsapp';

const REOPEN_MS = 10_000;
const META_WARN_MS = 5 * 24 * 60 * 60 * 1000;
const SUPPRESS_PAGES = new Set(['connections', 'onboarding']);

type IssueKind = Exclude<WhatsappStatusBucket, 'connected'>;
type WhatsAppIssue = { conn: WhatsappConnection; kind: IssueKind };

function issueKindFromStatus(status: string): IssueKind | null {
  const bucket = normalizeWhatsappStatusBucket(status);
  if (bucket === 'connected') return null;
  return bucket;
}

function findWhatsAppIssues(connections: WhatsappConnection[]): WhatsAppIssue[] {
  const issues: WhatsAppIssue[] = [];
  for (const conn of connections) {
    const kind = issueKindFromStatus(conn.status);
    if (kind) issues.push({ conn, kind });
  }
  return issues;
}

function buildWhatsAppCopy(missing: boolean, issues: WhatsAppIssue[]) {
  if (missing) {
    return {
      title: 'WhatsApp não conectado',
      description:
        'Nenhuma conexão WhatsApp ativa. Abra Canais de Atendimento para conectar e começar a receber mensagens.',
    };
  }

  if (issues.length === 1) {
    const { conn, kind } = issues[0];
    return kind === 'pending'
      ? {
          title: 'WhatsApp pendente',
          description: `A conexão "${conn.name}" ainda não foi concluída. Abra Canais de Atendimento para finalizar.`,
        }
      : {
          title: 'WhatsApp desconectado',
          description: `A conexão "${conn.name}" foi desconectada. Reconecte em Canais de Atendimento para continuar.`,
        };
  }

  const pending = issues.filter((i) => i.kind === 'pending').length;
  const disconnected = issues.length - pending;
  const parts = [
    disconnected > 0 && `${disconnected} desconectada${disconnected > 1 ? 's' : ''}`,
    pending > 0 && `${pending} pendente${pending > 1 ? 's' : ''}`,
  ].filter(Boolean);

  return {
    title: 'WhatsApp precisa de atenção',
    description: `Há ${parts.join(' e ')}. Abra Canais de Atendimento para corrigir.`,
  };
}

function metaTrackingAction(goTracking: () => void) {
  return { label: 'Página de Integração', onClick: goTracking };
}

export const GlobalConnectionAlerts: React.FC = () => {
  const { whatsappConnections, metaAccounts, isLoading } = useCrmData();
  const { currentOrganization, isLoading: isOrgLoading } = useOrganization();
  const { currentPage, setCurrentPage } = useAppContext();
  const { canManageIntegrations } = usePermissions();
  const [notifiedMeta, setNotifiedMeta] = useState<Set<string>>(() => new Set());
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const reopenTimerRef = useRef<number | null>(null);
  const shouldShowRef = useRef(false);

  const issues = useMemo(
    () => findWhatsAppIssues(whatsappConnections),
    [whatsappConnections],
  );
  const hasConnected = useMemo(
    () =>
      whatsappConnections.some((conn) => issueKindFromStatus(conn.status) === null),
    [whatsappConnections],
  );
  const whatsappStatusReady = !isLoading && !isOrgLoading && Boolean(currentOrganization);
  const missing = whatsappStatusReady && whatsappConnections.length === 0;
  const issueKey = missing
    ? 'missing'
    : issues.map((i) => `${i.conn.id}:${i.kind}`).join('|');
  // Só alerta quando não há nenhuma conexão saudável (alinha com Canais).
  const shouldShow =
    whatsappStatusReady &&
    !hasConnected &&
    (missing || issues.length > 0) &&
    !SUPPRESS_PAGES.has(currentPage);
  shouldShowRef.current = shouldShow;
  const copy = buildWhatsAppCopy(missing, issues);

  const clearReopenTimer = () => {
    if (reopenTimerRef.current === null) return;
    window.clearTimeout(reopenTimerRef.current);
    reopenTimerRef.current = null;
  };

  const scheduleReopen = () => {
    clearReopenTimer();
    reopenTimerRef.current = window.setTimeout(() => {
      reopenTimerRef.current = null;
      if (shouldShowRef.current) setWhatsAppModalOpen(true);
    }, REOPEN_MS);
  };

  useEffect(() => {
    clearReopenTimer();
    setWhatsAppModalOpen(shouldShow);
  }, [shouldShow, issueKey]);

  useEffect(() => () => clearReopenTimer(), []);

  const handleCloseWhatsAppModal = () => {
    setWhatsAppModalOpen(false);
    scheduleReopen();
  };

  const handleGoToChannels = () => {
    setWhatsAppModalOpen(false);
    clearReopenTimer();
    if (canManageIntegrations) {
      setCurrentPage('connections');
    }
  };

  useEffect(() => {
    if (!canManageIntegrations) return;

    const goTracking = () => setCurrentPage('tracking');
    const now = Date.now();

    metaAccounts?.forEach((meta) => {
      if (!meta.expiresOn) return;
      const timeLeft = new Date(meta.expiresOn).getTime() - now;

      if (timeLeft <= META_WARN_MS && !notifiedMeta.has(meta.id)) {
        if (timeLeft > 0) {
          const daysLeft = Math.ceil(timeLeft / (24 * 60 * 60 * 1000));
          notify.warning(
            `A integração do Meta expirará em ${daysLeft} dia${daysLeft > 1 ? 's' : ''}.`,
            { duration: Infinity, action: metaTrackingAction(goTracking) },
          );
        } else {
          notify.error('A integração do Meta expirou e precisa ser reconectada.', {
            duration: Infinity,
            action: metaTrackingAction(goTracking),
          });
        }
        setNotifiedMeta((prev) => new Set(prev).add(meta.id));
        return;
      }

      if (timeLeft > META_WARN_MS && notifiedMeta.has(meta.id)) {
        setNotifiedMeta((prev) => {
          const next = new Set(prev);
          next.delete(meta.id);
          return next;
        });
      }
    });
  }, [metaAccounts, setCurrentPage, notifiedMeta, canManageIntegrations]);

  return (
    <Modal
      isOpen={whatsAppModalOpen}
      onClose={handleCloseWhatsAppModal}
      size="sm"
      overlayClassName="items-center p-s4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))]"
      className="max-h-[calc(100dvh-2rem)] overflow-hidden rounded-2xl border-subtle"
    >
      <Modal.Header
        onClose={handleCloseWhatsAppModal}
        className="relative border-b border-subtle before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:bg-error before:content-['']"
      >
        {copy.title}
      </Modal.Header>
      <Modal.Body>
        <section
          className="flex flex-col items-center gap-s4 text-center"
          aria-live="assertive"
        >
          <div
            className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-danger-bg text-error sm:size-11"
            aria-hidden
          >
            <AppIcon name="alert-triangle" className="size-6" />
          </div>
          <div className="min-w-0 space-y-s2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-danger-text">
              Ação necessária
            </p>
            <p className="text-sm leading-relaxed text-secondary sm:text-[15px]">
              {copy.description}
            </p>
          </div>
        </section>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          variant="outline"
          className="min-h-11 w-full touch-manipulation sm:w-auto"
          onClick={handleCloseWhatsAppModal}
        >
          Fechar
        </Button>
        <Button
          type="button"
          className="min-h-11 w-full touch-manipulation sm:w-auto"
          onClick={handleGoToChannels}
        >
          {canManageIntegrations ? 'Ir para Canais' : 'Entendi'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GlobalConnectionAlerts;
